import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdShield, MdCheck, MdBlock } from 'react-icons/md';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDateTime, statusBadgeClass } from '../utils/format';

const TABS = [
  { key: 'all', label: 'All Installs' },
  { key: 'flagged', label: 'Flagged (Fraud)' },
];

export default function PartnerInstalls() {
  const [tab, setTab] = useState('all');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const { page, limit, setPage, setLimit } = usePagination(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchList = useCallback(async () => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      const params = { page, limit };
      if (tab === 'flagged') params.flagged = 'true';
      const res = await api.get('/admin/partner-installs', { params });
      setList(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      toast.error('Failed to load installs');
    } finally {
      setLoading(false);
    }
  }, [tab, page, limit]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const resolve = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/partner-installs/${id}/resolve`, { status });
      toast.success(status === 'valid' ? 'Marked valid' : 'Rejected');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusyId(null); }
  };

  const mask = (d) => (d && d.length > 8 ? `${d.slice(0, 4)}…${d.slice(-4)}` : d || '-');

  return (
    <div className="page">
      <div className="page-header"><h2>Install Tracking</h2></div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : list.length === 0 ? (
        <div className="empty-state"><MdShield className="empty-icon" /><h3>Nothing here</h3></div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Partner</th><th>Device</th><th>Source</th><th>Fraud</th><th>Installed</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {list.map((it) => (
                <tr key={it.id}>
                  <td>{it.partner?.partner_code || it.partner_id}<div className="text-muted" style={{ fontSize: 12 }}>{it.partner?.name}</div></td>
                  <td>{mask(it.device_id)}</td>
                  <td>{it.source || '-'}<div className="text-muted" style={{ fontSize: 12 }}>{it.attribution_type}</div></td>
                  <td>
                    <span className={`badge ${it.fraud_score >= 60 ? 'badge-danger' : it.fraud_score >= 30 ? 'badge-warning' : 'badge-neutral'}`}>
                      {it.fraud_score}
                    </span>
                    {Array.isArray(it.flag_reasons) && it.flag_reasons.length > 0 && (
                      <div className="text-muted" style={{ fontSize: 11 }}>{it.flag_reasons.join(', ')}</div>
                    )}
                  </td>
                  <td>{formatDateTime(it.install_time)}</td>
                  <td><span className={`badge ${statusBadgeClass(it.is_flagged ? 'flagged' : it.status)}`}>{it.is_flagged ? 'flagged' : it.status}</span></td>
                  <td>
                    {(it.is_flagged || it.status === 'flagged') && it.status !== 'rejected' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-ghost" disabled={busyId === it.id} title="Mark valid" onClick={() => resolve(it.id, 'valid')}><MdCheck /></button>
                        <button className="btn btn-sm btn-danger" disabled={busyId === it.id} title="Reject" onClick={() => resolve(it.id, 'rejected')}><MdBlock /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && list.length > 0 && (
        <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
      )}
    </div>
  );
}
